import * as React from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { systemClock } from '@reos/config';
import { Columns3, Ellipsis, Plus, Table as TableIcon } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { KanbanColumn } from '../components/kanban-column.js';
import { PageHead } from '../components/page-head.js';
import { listCases, transitionCase, type KanbanCase } from '../lib/cases-api.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { listOffices, type OfficeDto } from '../lib/offices-api.js';
import { listCaseCostsSummary, type CaseCostsSummaryItem } from '../lib/costs-api.js';
import { listTasks } from '../lib/tasks-api.js';
import { nextStepTaskByCase, type NextStepTaskInput } from '../lib/case-next-step.js';
import { listAuditRunSummary, recheckCaseAudit, type AuditRunSummaryItem } from '../lib/audit-api.js';
import { bulkRecheckEstimate, isStaleCase } from '../lib/case-freshness-filter.js';
import { getResolveUnknownStats } from '../lib/resolve-unknown-api.js';
import { loadCaseCovers } from '../lib/case-covers.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  KANBAN_STAGE_ORDER,
  CANCELLED_STAGE,
  KANBAN_CASE_TYPES,
  CASE_TYPE_LABELS,
  isStuckInStage,
  stageBadgeClassA2,
  stageHue,
  stageLabel,
  STAGE_STUCK_DAYS,
  type KanbanCaseType,
} from '../lib/case-stages.js';
import { caseBlockerLine } from '../lib/case-blocker.js';
import { caseProgressView } from '../lib/case-progress.js';
import { PropertyImage } from '../components/PropertyImage.js';
import { V4Pill, V4Progress } from '../components/ux-v4.js';
import { fetchPropertyBriefs, KIND_VALUES, kindLabel, type PropertyListItem } from '../lib/properties.js';
import { caseDisplayName, type CaseDisplayName, type CaseNameTexts } from '../lib/case-title.js';
// Krok TOKYFIX7: nejkratsi dotaz, se kterym ma hledani zakazky smysl — jedna
// hodnota pro vsechna hledani zakazek (vyber u faktury i tenhle prehled).
import { CASE_SEARCH_MIN_LENGTH } from '../lib/case-address-lookup.js';
import { buildCaseRows, sortCaseRows, type CaseSort, type CaseSortField, type CoverInfo } from '../lib/case-rows.js';
import { formatRelativeChange } from '../lib/relative-time.js';
import { formatDueDate } from '../lib/task-board.js';
import { useSession, readCaseViewPreference, writeCaseViewPreference } from '../stores/session.js';
import { useT } from '../lib/i18n.js';

type TypeFilter = 'all' | KanbanCaseType;

/** Kolik zakazek nacte jedna stranka kanbanu — hlaska o oreznuti pouziva tutez hodnotu. */
const CASE_PAGE_LIMIT = 100;

/**
 * Krok GRAF5: kolik radku tabulky stoji na jedne strance (predloha ma pod
 * tabulkou „‹ 1 2 3 ›"). Nacitani se tim nemeni — strankuje se az to, co uz
 * je nactene a vyfiltrovane, takze zadny dalsi dotaz na server nevznika.
 */
const CASE_ROWS_PER_PAGE = 25;

/** Krok TOKYFIX7: zdrzeni hledani. Tataz hodnota jako na tabuli ukolu (TOKYFIX2). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Krok UXR1 — ZOBRAZENI UZ JSOU DVE, NE TRI.
 *
 * Schvaleny mockup `02-zakazky.html` ma prepinac „Tabulka / Kanban" a jako
 * VYCHOZI tabulku (do UXR1 byl vychozi kanban a existovalo i treti zobrazeni
 * „Seznam"). Treti zobrazeni tim konci: zustat v repu bez volajiciho by bylo
 * mrtvy kod (CLAUDE.md pravidlo 8), a predloha ho nema.
 */
const CASE_VIEWS = ['table', 'kanban'] as const;
type CaseView = (typeof CASE_VIEWS)[number];

/**
 * Volba zobrazeni je predvolba prohlizece per uzivatel (klic
 * `reos.zakazky.view`); cte a zapisuje ji `stores/session.ts` — jedine misto
 * web-adminu, kde smi byt `localStorage` (krok S3, vynucuje eslint).
 */
function readStoredView(): CaseView {
  const raw = readCaseViewPreference();
  return raw !== null && (CASE_VIEWS as readonly string[]).includes(raw) ? (raw as CaseView) : 'table';
}

/**
 * Zahlavi raditelneho sloupce. Komponenta MUSI byt na urovni modulu, ne uvnitr
 * `ZakazkyPage`: vnorena definice je pri kazdem renderu jiny typ, takze React
 * `<th>` pokazde odmountuje a znovu vytvori — klik by pak chodil na uz
 * odpojene tlacitko a fokus by po kazdem razeni skocil pryc.
 */
function SortableHeader({
  field,
  label,
  sort,
  onToggle,
  style,
}: {
  readonly field: CaseSortField;
  readonly label: string;
  readonly sort: CaseSort | null;
  readonly onToggle: (field: CaseSortField) => void;
  readonly style?: React.CSSProperties;
}): React.JSX.Element {
  const active = sort?.field === field ? sort.dir : null;
  return (
    <th
      {...(style !== undefined ? { style } : {})}
      aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
    >
      <button type="button" className="rh-a2-th-sort" onClick={() => onToggle(field)}>
        {label}
        {active !== null ? <span aria-hidden="true">{active === 'asc' ? ' ↑' : ' ↓'}</span> : null}
      </button>
    </th>
  );
}

/**
 * `/zakazky` — zakazky podle SCHVALENEHO mockupu `02-zakazky.html` (krok
 * UXR1): vychozi tabulka (adresa + popis, typ, maklér, faze jako stitek,
 * dalsi krok, posledni zmena, „⋯"), rychle filtry Moje / Celá kancelář,
 * stitky fazi s pocty a prepinac Tabulka / Kanban.
 *
 * Kanban: sloupce = brokerage stage, presun = POST /v1/cases/:id/transition.
 * Nepovoleny prechod se nenabidne: `allowedTransitions` na kazde karte chodi
 * z API (state-machine), UI ho jen cte pri rozhodovani, jestli drop prijmout
 * (viz handleDragEnd).
 */
export function ZakazkyPage(): React.JSX.Element {
  const currentUserId = useSession((s) => s.user?.id ?? null);
  const t = useT();
  const navigate = useNavigate();
  const [cases, setCases] = React.useState<KanbanCase[] | null>(null);
  const [members, setMembers] = React.useState<TenantMember[]>([]);
  /** Krok QAFIX4b bod 3: pobočky pro filtr. Prázdné = kancelář žádnou nemá. */
  const [officeOptions, setOfficeOptions] = React.useState<readonly OfficeDto[]>([]);
  const [costsByCase, setCostsByCase] = React.useState<ReadonlyMap<string, CaseCostsSummaryItem>>(new Map());
  const [coverByProperty, setCoverByProperty] = React.useState<ReadonlyMap<string, CoverInfo>>(new Map());
  /**
   * Krok TAB1 bod 9: JEDNO davkove nacteni nemovitosti pro VSECHNO, co z nich
   * seznam potrebuje — adresu zakazky bez nazvu (krok O8), druh nemovitosti a
   * obec do radku pod adresou na karte. Do TAB1 se stahovaly jen adresy, a to
   * jen pro zakazky se zastupnym nazvem.
   */
  const [briefByProperty, setBriefByProperty] = React.useState<ReadonlyMap<string, PropertyListItem>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [truncated, setTruncated] = React.useState(false);

  const [mineOnly, setMineOnly] = React.useState(false);
  const [stageFilter, setStageFilter] = React.useState<string | 'all'>('all');
  const [brokerId, setBrokerId] = React.useState('');
  /**
   * Krok QAFIX4b bod 3 (mezera POB1): filtr podle POBOCKY. Filtruje se nad
   * UZ NACTENYM seznamem, stejne jako filtr „dle makléře" vedle nej — seznam
   * uz prisel zuzeny dosahem serveru (`brokerOfficeListScope`), takze tohle
   * je jen vyber vedouciho, ktery vidi vic pobocek.
   */
  const [officeFilter, setOfficeFilter] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');
  /** Krok TAB1 bod 7 / UXK3c bod 2: druh nemovitosti — od UXK3c vyber, ne cipy. */
  const [kindFilter, setKindFilter] = React.useState<string | 'all'>('all');
  /** Krok UXK3c bod 2: na mobilu jsou vybery druhu a faze az za „Filtry". */
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  /** Krok TAB1 bod 7: cerveny cip „Jen stojící > 30 dní". */
  const [stuckOnly, setStuckOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  /**
   * Krok TOKYFIX7 (prekazka P2/6) — HLEDANI JDE NA SERVER, PRES VSECHNY
   * STRANKY. `search` je to, co makleřka prave pise; `searchQuery` je dotaz,
   * se kterym je nactena stranka (prazdny = vychozi pohled). Zdrzeni i tvar
   * jsou tytez jako na tabuli ukolu (`routes/ukoly.tsx`, TOKYFIX2) — druhy
   * zpusob, jak hledat v prehledu, nevznika.
   */
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showCancelled, setShowCancelled] = React.useState(false);
  // Krok UX2: filtr „provereno pred vice nez 14 dny" + hromadna akce.
  const [staleOnly, setStaleOnly] = React.useState(false);
  const [freshnessByCase, setFreshnessByCase] = React.useState<ReadonlyMap<string, AuditRunSummaryItem>>(new Map());
  /**
   * Krok FIX6 bod 7: „Další krok" v tabulce. `CaseDto.nextTaskTitle` se na
   * serveru pocita z `case_tasks`, ktere ale uz nikdo nepise (ukoly makleru
   * jdou do `crm_tasks`) — sloupec proto byl u VSECH zakazek „—". Pary
   * caseId -> titul nejblizsiho otevreneho ukolu z zive cesty.
   */
  const [nextStepByCaseId, setNextStepByCaseId] = React.useState<ReadonlyMap<string, string>>(new Map());
  /**
   * Krok UXF5 bod 5 (nalez #12): tentyz ukol CELY — sloupec „Další krok" ma
   * vedle vety ukazat termin a odpovedneho maklere (nebo vyslovne, ze je
   * nema). Oba stavy jdou z JEDNOHO vyberu (`nextStepTaskByCase`).
   */
  const [nextStepTaskByCaseId, setNextStepTaskByCaseId] = React.useState<
    ReadonlyMap<string, NextStepTaskInput>
  >(new Map());
  const [bulkConfirm, setBulkConfirm] = React.useState(false);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [bulkNotice, setBulkNotice] = React.useState<string | null>(null);

  const [view, setView] = React.useState<CaseView>(readStoredView);
  const [openRowMenu, setOpenRowMenu] = React.useState<string | null>(null);
  // Krok O8: razeni tabulky zustava (lokalni nad nactenou strankou). Mockup
  // razeni nekresli, proto je zahlavi tlacitko bez vlastni vyzdoby — stav
  // nese `aria-sort` a sipka az u aktivniho sloupce.
  const [tableSort, setTableSort] = React.useState<CaseSort | null>(null);

  const [draggingAllowed, setDraggingAllowed] = React.useState<readonly string[] | null>(null);
  const [now] = React.useState(() => systemClock.now());
  /**
   * Krok K0e (ADM1 bod 5 - oprava): kolik ukolu fronty „K doplneni" ceka na
   * cloveka. `getResolveUnknownStats().tasksOpen` pocita presne to, co
   * `/k-doplneni` vypisuje.
   */
  const [waitingHumanTasksCount, setWaitingHumanTasksCount] = React.useState(0);
  // Aktivacni prah (8px) - bez nej PointerSensor pohlti i obycejny klik na
  // kartu (chce ho zvednout jako drag), takze "klik -> detail" by nikdy
  // nedosel do KanbanCard.onClick.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      /*
       * Krok TOKYFIX7 (prekazka P2/6, mereni 3. 11. 2026 12:45) — NEJNOVEJSI
       * ZAKAZKY NAHORU.
       *
       * Vychozi razeni serveru je `created_at asc` (strankovaci poradi), takze
       * jedna stranka o 100 radcich vratila v kancelari KR1 se 165 zakazkami
       * 100 NEJSTARSICH — zakazka, kterou makleřka prave zalozila, na
       * obrazovce nebyla vubec („zakázka ‚Vinohradská 52/1' není v přehledu
       * Zakázky"). `updated_desc` je naposledy zmenene (tedy i prave
       * zalozene) nahore; je JEDNOSTRANKOVE (`nextCursor` vzdy `null`, viz
       * `lib/cases-api.ts`), proto se oreznuti pozna az z poctu radku.
       */
      const [casesResult, membersResult] = await Promise.all([
        listCases({
          limit: CASE_PAGE_LIMIT,
          sort: 'updated_desc',
          ...(searchQuery !== '' ? { q: searchQuery } : {}),
        }),
        listMembers(),
      ]);
      setCases(casesResult.items);
      setTruncated(casesResult.items.length >= CASE_PAGE_LIMIT);
      setMembers(membersResult);
      /*
       * Pobočky jsou DOPLNĚK filtru, ne podmínka seznamu: když jejich načtení
       * selže (starší API, chybějící oprávnění), zůstane přepínač skrytý a
       * zakázky se vykreslí — `Promise.all` výš by kvůli nim spadl celý list.
       */
      void listOffices(false).then(setOfficeOptions, () => setOfficeOptions([]));
    } catch (e) {
      setLoadError(e instanceof DomainApiError ? e.message : t('webAdmin.kanban.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  React.useEffect(() => {
    void load();
  }, [load]);

  /*
   * Krok TOKYFIX7 — dotaz se odesila se zdrzenim `SEARCH_DEBOUNCE_MS`
   * (makleřka pise) a jen kdyz ma aspoň `CASE_SEARCH_MIN_LENGTH` znaku:
   * kratsi `q` server odmita (400, `zod.string().min(2)`), takze by se
   * z hledani stal prazdny seznam s chybou.
   */
  React.useEffect(() => {
    const trimmed = search.trim();
    const next = trimmed.length >= CASE_SEARCH_MIN_LENGTH ? trimmed : '';
    if (next === searchQuery) return;
    // Zrusene hledani („Vymazat filtry") se vraci k vychozimu pohledu HNED —
    // jinak by na obrazovce zustal vysledek dotazu, ktery uz v poli neni.
    if (next === '') {
      setSearchQuery('');
      return;
    }
    const timer = setTimeout(() => setSearchQuery(next), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, searchQuery]);

  // K0e: kosmetika prehledu - kdyz volani selze, odznak se jen nezobrazi.
  React.useEffect(() => {
    let cancelled = false;
    void getResolveUnknownStats()
      .then((stats) => {
        if (!cancelled) setWaitingHumanTasksCount(stats.tasksOpen);
      })
      .catch(() => {
        if (!cancelled) setWaitingHumanTasksCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // R5 + O6a (polozka 3): naklady/marze na kartach - jeden hromadny request
  // pro vsechny nactene zakazky misto N volani `/v1/cases/:id/costs`.
  React.useEffect(() => {
    if (cases === null || cases.length === 0) {
      setCostsByCase(new Map());
      return;
    }
    let cancelled = false;
    void listCaseCostsSummary(cases.map((c) => c.id))
      .then((items) => {
        if (!cancelled) setCostsByCase(new Map(items.map((i) => [i.caseId, i])));
      })
      .catch(() => {
        if (!cancelled) setCostsByCase(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  /*
   * Krok FIX6 bod 7: ukoly makleru pro sloupec „Další krok" — JEDEN dotaz na
   * stranku, stejny vzor jako naklady vyse. Selhani je kosmetika: sloupec
   * zustane na serverove hodnote (`nextTaskTitle`), stranka se nerozbije.
   */
  React.useEffect(() => {
    if (cases === null || cases.length === 0) {
      setNextStepByCaseId(new Map());
      setNextStepTaskByCaseId(new Map());
      return;
    }
    let cancelled = false;
    void listTasks({ limit: CASE_PAGE_LIMIT * 2 })
      .then((result) => {
        if (cancelled) return;
        const byCase = nextStepTaskByCase(result.items);
        setNextStepTaskByCaseId(byCase);
        setNextStepByCaseId(new Map([...byCase].map(([id, task]) => [id, task.title])));
      })
      .catch(() => {
        if (!cancelled) {
          setNextStepByCaseId(new Map());
          setNextStepTaskByCaseId(new Map());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  // Krok UX2: „kdy naposledy provereno" pro filtr a hromadnou akci — JEDEN
  // davkovy request na celou stranku, stejny duvod jako u nakladu vyse.
  React.useEffect(() => {
    if (cases === null || cases.length === 0) {
      setFreshnessByCase(new Map());
      return;
    }
    let cancelled = false;
    void listAuditRunSummary(cases.map((c) => c.id))
      .then((items) => {
        if (!cancelled) setFreshnessByCase(new Map(items.map((i) => [i.dealId, i])));
      })
      .catch(() => {
        if (!cancelled) setFreshnessByCase(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  // O6/DEMO1/DEMO2: titulni fotky, mapa adresy a vyrez obce — jedno davkove
  // volani pro vsechny nactene karty (`lib/case-covers.ts`).
  React.useEffect(() => {
    const propertyIds = (cases ?? []).map((c) => c.propertyId).filter((id): id is string => id !== null);
    if (propertyIds.length === 0) {
      setCoverByProperty(new Map());
      return;
    }
    let cancelled = false;
    void loadCaseCovers(propertyIds, (partial) => {
      // Fotky a souradnice se ukazou hned, jak je server vrati.
      if (!cancelled) setCoverByProperty(partial);
    })
      .then((covers) => {
        if (!cancelled) setCoverByProperty(covers);
      })
      .catch(() => {
        if (!cancelled) setCoverByProperty(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  /*
   * O8 + krok TAB1 bod 9: nemovitosti zakazek jednim strankovanym volanim.
   * Do TAB1 se tahaly jen adresy a jen pro zakazky se zastupnym nazvem; karta
   * kanbanu ale potrebuje druh a obec u KAZDE zakazky („Pozemek · prodej ·
   * Lysá n. L.") a filtr podle druhu nemovitosti taky. Je to tentyz
   * `fetchPropertyBriefs`, jaky uz pouziva Dnesek (krok UXR5 bod 3), takze
   * nevznika druhy zpusob, jak se na nemovitost zeptat.
   */
  React.useEffect(() => {
    const propertyIds = (cases ?? []).map((c) => c.propertyId).filter((id): id is string => id !== null);
    if (propertyIds.length === 0) {
      setBriefByProperty(new Map());
      return;
    }
    let cancelled = false;
    void fetchPropertyBriefs(propertyIds)
      .then((found) => {
        if (!cancelled) setBriefByProperty(found);
      })
      .catch(() => {
        if (!cancelled) setBriefByProperty(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [cases]);

  const memberName = React.useCallback(
    (userId: string | null): string | null => {
      if (userId === null) return null;
      return members.find((m) => m.userId === userId)?.name ?? null;
    },
    [members],
  );

  /**
   * Krok UXF5 bod 5 (nalez #12) — termin dalsiho kroku. `formatDueDate` je
   * TENTYZ zapis, jaky ukazuje karta na tabuli ukolu; druhy format datumu
   * u teze veci by se rozesel.
   */
  const nextStepDue = React.useCallback(
    (dueAt: string | null): string => {
      const text = formatDueDate(dueAt);
      return text === '' ? t('webAdmin.tasks.noDue') : text;
    },
    [t],
  );

  const nameTexts = React.useMemo<CaseNameTexts>(
    () => ({
      untitled: t('webAdmin.kanban.untitled'),
      untitledHint: (id) => t('webAdmin.kanban.untitledHint', { id }),
    }),
    [t],
  );

  /** Adresy pro `caseDisplayName` (krok O8) — z tehoz davkoveho nacteni. */
  const addressByProperty = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, item] of briefByProperty) if (item.address !== '') map.set(id, item.address);
    return map;
  }, [briefByProperty]);

  // Nazev se pocita jednou pro vsechna zobrazeni I pro hledani — jinak by
  // hledani pracovalo s jinym textem, nez makler na obrazovce vidi.
  const displayNames = React.useMemo(() => {
    const map = new Map<string, CaseDisplayName>();
    for (const c of cases ?? []) map.set(c.id, caseDisplayName(c, addressByProperty, nameTexts));
    return map;
  }, [cases, addressByProperty, nameTexts]);

  const displayName = React.useCallback(
    (item: KanbanCase): CaseDisplayName => displayNames.get(item.id) ?? caseDisplayName(item, addressByProperty, nameTexts),
    [displayNames, addressByProperty, nameTexts],
  );

  /**
   * Krok TOKYFIX7 — KDO PRAVE HLEDA: SERVER, NEBO OBRAZOVKA?
   *
   * Kdyz je nactena stranka odpovedi na PRESNE ten dotaz, ktery je v poli,
   * uz hledal server — a hledal siroc: vedle nazvu i v ulici, cisle, PSC a
   * obci nemovitosti (`listCases` `q`). Dalsi filtrovani nad nazvem by prave
   * tyhle nalezy zahodilo (zakazka se zastupnym nazvem „Nemovitost 01a0…"
   * dotaz na ulici v textu nema), proto se nad odpovedi serveru uz
   * nefiltruje. Dokud dotaz nedosel na server, filtruje obrazovka nactenou
   * stranku — makleřka tak vidi odezvu hned pri psani.
   */
  const serverSearched = searchQuery !== '' && search.trim() === searchQuery && !loading;

  /** Dotaz je napsany, ale odpoved serveru na nem jeste neni. */
  const searchPending =
    (search.trim().length >= CASE_SEARCH_MIN_LENGTH && search.trim() !== searchQuery) ||
    (loading && searchQuery !== '');

  /** Vsechny filtry KROME faze a aktualnosti — z toho se pocitaji stitky fazi. */
  const beforeStageFilter = React.useMemo(() => {
    if (cases === null) return [];
    const q = serverSearched ? '' : search.trim().toLowerCase();
    return cases.filter((c) => {
      if (!(KANBAN_CASE_TYPES as readonly string[]).includes(c.type)) return false;
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      if (mineOnly && c.ownerUserId !== currentUserId) return false;
      if (brokerId !== '' && c.ownerUserId !== brokerId) return false;
      if (officeFilter !== '' && c.officeId !== officeFilter) return false;
      /*
       * Krok TAB1 bod 7: druh nemovitosti. Zakazka, u ktere jeste nevime, co
       * je zac (nemovitost se nenacetla), se pri zapnutem filtru NEUKAZE —
       * jinak by cip „Pozemek" ukazal i byty, dokud se seznam nedotahne.
       */
      if (kindFilter !== 'all') {
        const brief = c.propertyId === null ? undefined : briefByProperty.get(c.propertyId);
        if (brief?.kind !== kindFilter) return false;
      }
      // Krok TAB1 bod 7: „Jen stojící > 30 dní" (jedna funkce — `isStuckInStage`).
      if (stuckOnly && !isStuckInStage(c.stageEnteredAt, now)) return false;
      // Zrusene se skryvaji uz tady, aby filtr platil v tabulce i v kanbanu.
      if (!showCancelled && c.stage === CANCELLED_STAGE) return false;
      if (q !== '') {
        const name = displayNames.get(c.id)?.text ?? c.title;
        if (!name.toLowerCase().includes(q) && !c.title.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [
    cases,
    typeFilter,
    kindFilter,
    stuckOnly,
    now,
    briefByProperty,
    mineOnly,
    brokerId,
    officeFilter,
    search,
    serverSearched,
    currentUserId,
    showCancelled,
    displayNames,
  ]);

  /**
   * Stitky fazi s pocty (mockup: „Vše 23 · Nábor 3 · Inzerujeme 9 …").
   * ROZHODNUTI UXR1-3: mockup vyjmenovava sest ilustrativnich fazi, skutecny
   * stavovy automat jich ma deset (`KANBAN_STAGE_ORDER`). Vymyslet nove nazvy
   * fazi by znamenalo lhat o stavu zakazky, takze stitky nesou SKUTECNE faze
   * — a jen ty, ve kterych neco je (prazdny stitek nikomu nepomuze).
   */
  /**
   * Pocty po fazich. JEDNA mapa pro cipy filtru i pro dlazdice s cisly nad
   * tabulkou (krok GRAF5) — dva vypocty tehoz by se rozesly hned, jakmile by
   * jeden z nich zacal nebo prestal pocitat zrusene zakazky.
   */
  const stageCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of beforeStageFilter) counts.set(c.stage, (counts.get(c.stage) ?? 0) + 1);
    return counts;
  }, [beforeStageFilter]);

  const stageChips = React.useMemo(() => {
    const stages = showCancelled ? [...KANBAN_STAGE_ORDER, CANCELLED_STAGE] : KANBAN_STAGE_ORDER;
    return stages
      .map((stage) => ({ stage, count: stageCounts.get(stage) ?? 0 }))
      .filter(({ stage, count }) => count > 0 || stage === stageFilter);
  }, [stageCounts, showCancelled, stageFilter]);

  /*
   * Krok GRAF5 — RADA CTYR DLAZDIC NAD TABULKOU (predloha
   * `/srv/debata/maklerov-grafika-zakazky`). Cisla jsou ze SKUTECNYCH dat
   * seznamu, ne z vlastniho dotazu: „Otevřené" je tentyz pocet, jaky nese
   * podtitulek hlavicky (`totalOpen`), zbyle tri jsou faze z `stageCounts`.
   *
   * ROZHODNUTI GRAF5-DLAZDICE-FAZE: predloha pise u treti dlazdice
   * „Prohlídky tento týden". Seznam zakazek kalendar NENACITA, takze
   * „tento týden" by byl udaj, ktery obrazovka nema odkud vzit; dlazdice
   * proto nese POCET ZAKAZEK VE FAZI (tentyz popisek jako kapsle v radku)
   * a o zadnem tydnu netvrdi nic. Klik na dlazdici filtruje tutez fazi —
   * neni to druhy ovladac, je to druha cesta k `stageFilter`.
   */
  const stageTiles = React.useMemo(
    () =>
      (['listing', 'viewings', 'contracting'] as const).map((stage) => ({
        stage,
        label: stageLabel(stage),
        count: stageCounts.get(stage) ?? 0,
      })),
    [stageCounts],
  );

  /** Vsechny filtry KROME aktualnosti (odhad hromadne akce ji musi videt). */
  const filteredIgnoringStale = React.useMemo(
    () => (stageFilter === 'all' ? beforeStageFilter : beforeStageFilter.filter((c) => c.stage === stageFilter)),
    [beforeStageFilter, stageFilter],
  );

  const filtered = React.useMemo(() => {
    if (!staleOnly) return filteredIgnoringStale;
    return filteredIgnoringStale.filter((c) => {
      const summary = freshnessByCase.get(c.id);
      // Bez odpovedi ze serveru o zakazce nic nevime — netvrdime, ze je
      // zastarala, a do filtru ji nedavame.
      return summary !== undefined && isStaleCase(summary, now);
    });
  }, [filteredIgnoringStale, staleOnly, freshnessByCase, now]);

  const rows = React.useMemo(
    () => buildCaseRows({ cases: filtered, ownerName: memberName, displayName, coverByProperty, now }),
    [filtered, memberName, displayName, coverByProperty, now],
  );
  const sortedRows = React.useMemo(() => sortCaseRows(rows, tableSort), [rows, tableSort]);

  /*
   * Krok GRAF5 — STRANKOVANI POD TABULKOU (predloha `…-zakazky`: „‹ 1 2 3 ›").
   * Strankuje se az nad `CASE_ROWS_PER_PAGE` radky; kratsi seznam zadnou
   * stranku nema a pruh se nevykresli vubec (prazdne „1" nikomu nepomuze).
   * Popisky jsou TYTEZ klice, jake uz pouziva seznam nemovitosti
   * (`webAdmin.zakazky.table.*`) — zadna druha sada nazvu pro tentyz pojem.
   */
  const [page, setPage] = React.useState(1);
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / CASE_ROWS_PER_PAGE));
  /* Filtr zkratil seznam pod aktualni stranku — vracime se na posledni. */
  const safePage = Math.min(page, pageCount);
  const pagedRows = React.useMemo(
    () => sortedRows.slice((safePage - 1) * CASE_ROWS_PER_PAGE, safePage * CASE_ROWS_PER_PAGE),
    [sortedRows, safePage],
  );

  const filtersActive =
    mineOnly ||
    brokerId !== '' ||
    officeFilter !== '' ||
    typeFilter !== 'all' ||
    kindFilter !== 'all' ||
    stuckOnly ||
    search.trim() !== '' ||
    showCancelled ||
    staleOnly ||
    stageFilter !== 'all';

  const clearFilters = React.useCallback((): void => {
    setMineOnly(false);
    setBrokerId('');
    setOfficeFilter('');
    setTypeFilter('all');
    setKindFilter('all');
    setStuckOnly(false);
    setSearch('');
    setShowCancelled(false);
    setStaleOnly(false);
    setStageFilter('all');
  }, []);

  const changeView = React.useCallback((next: CaseView): void => {
    setView(next);
    writeCaseViewPreference(next);
  }, []);

  const columns = React.useMemo(() => {
    const stages = showCancelled ? [...KANBAN_STAGE_ORDER, CANCELLED_STAGE] : KANBAN_STAGE_ORDER;
    return stages
      .map((stage) => ({ stage, items: filtered.filter((c) => c.stage === stage) }))
      // Mockup ukazuje jen sloupce, ve kterych neco je; prazdny sloupec se
      // kresli jen pri presunu (musi byt kam pustit) nebo kdyz na nej filtruju.
      .filter(({ stage, items }) => items.length > 0 || draggingAllowed !== null || stage === stageFilter);
  }, [filtered, showCancelled, draggingAllowed, stageFilter]);

  /**
   * Krok UX2 — HROMADNE PREPROVERENI, NIKDY AUTOMATICKY. Odhad v dialogu a
   * seznam, ktery se opravdu spusti, jdou ze STEJNE funkce.
   */
  const bulkEstimate = React.useMemo(() => {
    const items = filteredIgnoringStale
      .map((c) => freshnessByCase.get(c.id))
      .filter((row): row is AuditRunSummaryItem => row !== undefined);
    return bulkRecheckEstimate(items, now);
  }, [filteredIgnoringStale, freshnessByCase, now]);

  const runBulkRecheck = React.useCallback((): void => {
    setBulkConfirm(false);
    setBulkBusy(true);
    setBulkNotice(null);
    const targets = bulkEstimate.dealIds;
    const byId = new Map((cases ?? []).map((c) => [c.id, c]));
    // Sekvencne, ne `Promise.all`: davka desitek behu najednou je presne to,
    // kvuli cemu se uz jednou zvedal RATE_LIMIT_MAX (kroky R5 / O6a).
    void (async () => {
      let ok = 0;
      for (const dealId of targets) {
        const zakazka = byId.get(dealId);
        if (zakazka === undefined) continue;
        try {
          await recheckCaseAudit({
            caseId: dealId,
            // Typ se NEPOSILA: seznam ho nezna (je az v detailu) a hadat ho
            // nesmi — spatny typ zakazky je prave ta vada, kterou UX2 resi.
            addressQuery: displayNames.get(dealId)?.text ?? zakazka.title,
            parcels: [],
            cadastralArea: null,
            landRegistryNumber: null,
          });
          ok += 1;
        } catch {
          // Jedna zakazka navic neshodi celou davku; vysledek rekne kolik z kolika.
        }
      }
      setBulkNotice(t('webAdmin.caseListFreshness.bulkQueued', { ok: String(ok), total: String(targets.length) }));
      setBulkBusy(false);
      void load();
    })();
  }, [bulkEstimate, cases, displayNames, load, t]);

  function handleDragStart(event: DragStartEvent): void {
    setActionError(null);
    const data = event.active.data.current as { allowedTransitions?: readonly string[] } | undefined;
    setDraggingAllowed(data?.allowedTransitions ?? []);
  }

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    setDraggingAllowed(null);
    const { active, over } = event;
    if (over === null) return;
    const targetStage = String(over.id);
    const data = active.data.current as { stage?: string; allowedTransitions?: readonly string[] } | undefined;
    if (data?.stage === targetStage) return;
    if (!data?.allowedTransitions?.includes(targetStage)) return;
    const caseId = String(active.id);
    try {
      const updated = await transitionCase(caseId, targetStage);
      setCases((prev) => (prev === null ? prev : prev.map((c) => (c.id === updated.id ? updated : c))));
    } catch (e) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.kanban.moveFailed'));
    }
  }

  // R4 + ROZHODNUTI S3: kanban je SEZNAM, detail je stranka `/zakazky/:id`.
  const openCase = React.useCallback(
    (id: string): void => {
      void navigate({ to: '/zakazky/$id', params: { id } });
    },
    [navigate],
  );

  /*
   * Krok TAB1 bod 6 — HLAVICKA: „N otevřených · N tvoje · N stojí déle než 30
   * dní". Vsechna tri cisla se pocitaji nad TYMZ zakladem (otevrene brokerage
   * zakazky bez zrusenych) a nezavisle na filtrech — hlavicka popisuje
   * kancelar, ne aktualni vyber. Pocty VE SLOUPCICH se naopak ridi filtrem
   * (ROZHODNUTI TAB1-POCTY v `kanban-column.tsx`).
   */
  const openCases = (cases ?? []).filter(
    (c) => (KANBAN_CASE_TYPES as readonly string[]).includes(c.type) && c.stage !== CANCELLED_STAGE,
  );
  const totalOpen = openCases.length;
  const mineCount = openCases.filter((c) => c.ownerUserId === currentUserId).length;
  const stuckCount = openCases.filter((c) => isStuckInStage(c.stageEnteredAt, now)).length;

  const relativeTexts = {
    today: (time: string) => t('webAdmin.relativeTime.today', { time }),
    yesterday: (time: string) => t('webAdmin.relativeTime.yesterday', { time }),
  };

  /** Klik na zahlavi: vzestupne → sestupne → bez razeni (poradi z API). */
  const toggleSort = React.useCallback((field: CaseSortField): void => {
    setTableSort((prev) => {
      if (prev === null || prev.field !== field) return { field, dir: 'asc' };
      if (prev.dir === 'asc') return { field, dir: 'desc' };
      return null;
    });
  }, []);

  // Prazdny stav je spolecny pro obe zobrazeni. Po filtru bez vysledku
  // nabidne cestu zpet (zadani O8, bod 3), jinak zustava puvodni hlaska.
  const emptyState = (
    <div className="rh-a2-empty" data-testid="zakazky-empty">
      {filtersActive ? (
        <>
          <p className="rh-a2-empty__title">{t('webAdmin.kanban.filteredEmptyTitle')}</p>
          <button type="button" className="rh-a2-btn" data-testid="clear-filters" onClick={clearFilters}>
            {t('webAdmin.kanban.clearFilters')}
          </button>
        </>
      ) : (
        <>
          <p className="rh-a2-empty__title">{t('webAdmin.kanban.emptyTitle')}</p>
          <p>{t('webAdmin.kanban.emptyDescription')}</p>
        </>
      )}
    </div>
  );

  return (
    <>
      <PageHead
        title={t('webAdmin.caseTexts.sectionTitle')}
        subtitle={t('webAdmin.zakazky.subtitleBoard', {
          open: totalOpen,
          mine: mineCount,
          stuck: stuckCount,
          days: STAGE_STUCK_DAYS,
        })}
        action={
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            data-testid="case-new"
            onClick={() => void navigate({ to: '/zakazky/novy' })}
          >
            <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
              <Plus size={15} strokeWidth={2} />
            </span>
            {t('webAdmin.caseListTexts.newCase')}
          </button>
        }
      />

      <div className="rh-a2-page rh-a2-page--wide" data-testid="zakazky-page">
        <div className="rh-a2-filters">
          <div className="rh-a2-seg" role="group" aria-label={t('webAdmin.kanban.brokerFilterAriaLabel')}>
            <button
              type="button"
              className={mineOnly ? 'is-on' : ''}
              onClick={() => setMineOnly(true)}
              aria-pressed={mineOnly}
              data-testid="filter-mine"
            >
              {t('webAdmin.kanban.mine')}
            </button>
            <button
              type="button"
              className={!mineOnly ? 'is-on' : ''}
              onClick={() => setMineOnly(false)}
              aria-pressed={!mineOnly}
              data-testid="filter-office"
            >
              {t('webAdmin.kanban.wholeOffice')}
            </button>
          </div>

          <span className="rh-a2-filters__sep" />

          {/*
            Krok UXK3c bod 2 — DRUH A FAZE JSOU DVA VYBERY, ne dve rady cipu.
            Do UXK3c mel filtracni pruh pet cipu druhu a az devet cipu fazi;
            na 390 px se zalamoval na ctyri rady a vlastni obsah stranky zacinal
            pod prehybem. Na mobilu jsou oba vybery AZ ZA tlacitkem „Filtry"
            (`rh-a2-filters__more`), na desktopu jsou videt vzdy.

            Popisky zustavaji TYTEZ: hodnoty ciselniku (`KIND_VALUES`:
            Pozemek / Budova / Jednotka / Komplex) pres `kindLabel`, faze pres
            `stageLabel`. ROZHODNUTI TAB1-DRUHY a UXK4 (nalez 11) zamerne
            NEprejmenovaly `unit` na „Byt" — „Byt" by byla lez o kazde
            jednotce, ktera bytem neni (garaz, sklep, nebytovy prostor) —
            a tento krok to nemeni ani nezaklada druhou sadu nazvu.
          */}
          <button
            type="button"
            className={`rh-a2-chip rh-a2-filters__more${filtersOpen ? ' is-on' : ''}`}
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="zakazky-quick-filters"
            data-testid="filters-toggle"
          >
            {t('webAdmin.zakazky.quickFilters')}
          </button>

          <div
            id="zakazky-quick-filters"
            className={`rh-a2-filters__group${filtersOpen ? ' is-open' : ''}`}
            data-testid="quick-filters"
          >
            <label className="rh-a2-field">
              <span>{t('webAdmin.kanban.columnKind')}</span>
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                data-testid="kind-select"
                aria-label={t('webAdmin.kanban.kindFilterAriaLabel')}
              >
                <option value="all">{t('webAdmin.kanban.allKinds')}</option>
                {KIND_VALUES.map((kind) => (
                  <option key={kind} value={kind}>
                    {kindLabel(kind)}
                  </option>
                ))}
              </select>
            </label>

          </div>

          {/* Prodej / Pronajem — tytez dve hodnoty, jake uz zna `typeFilter`. */}
          <button
            type="button"
            className={`rh-a2-chip${typeFilter === 'brokerage_sale' ? ' is-on' : ''}`}
            onClick={() => setTypeFilter(typeFilter === 'brokerage_sale' ? 'all' : 'brokerage_sale')}
            aria-pressed={typeFilter === 'brokerage_sale'}
            data-testid="deal-chip-sale"
          >
            {t('webAdmin.kanban.dealSale')}
          </button>
          <button
            type="button"
            className={`rh-a2-chip${typeFilter === 'brokerage_rent' ? ' is-on' : ''}`}
            onClick={() => setTypeFilter(typeFilter === 'brokerage_rent' ? 'all' : 'brokerage_rent')}
            aria-pressed={typeFilter === 'brokerage_rent'}
            data-testid="deal-chip-rent"
          >
            {t('webAdmin.kanban.dealRent')}
          </button>

          {/* Krok TAB1 bod 7: cerveny cip. Pocet je tentyz jako v hlavicce. */}
          <button
            type="button"
            className={`rh-a2-chip rh-a2-chip--bad${stuckOnly ? ' is-on' : ''}`}
            onClick={() => setStuckOnly((v) => !v)}
            aria-pressed={stuckOnly}
            title={t('webAdmin.kanban.stuckChipHint', { days: STAGE_STUCK_DAYS })}
            data-testid="stuck-chip"
          >
            {t('webAdmin.kanban.stuckChip', { days: STAGE_STUCK_DAYS })}{' '}
            <span className="rh-a2-chip__n">{stuckCount}</span>
          </button>

          <div className="rh-a2-seg rh-a2-filters__right" role="group" aria-label={t('webAdmin.kanban.viewSwitchLabel')}>
            <button
              type="button"
              className={view === 'table' ? 'is-on' : ''}
              onClick={() => changeView('table')}
              aria-pressed={view === 'table'}
              data-testid="view-table"
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <TableIcon size={15} strokeWidth={1.75} />
              </span>
              {t('webAdmin.kanban.viewTable')}
            </button>
            <button
              type="button"
              className={view === 'kanban' ? 'is-on' : ''}
              onClick={() => changeView('kanban')}
              aria-pressed={view === 'kanban'}
              data-testid="view-kanban"
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <Columns3 size={15} strokeWidth={1.75} />
              </span>
              {t('webAdmin.kanban.viewKanban')}
            </button>
          </div>
        </div>

        {/*
          Krok GRAF5 — CIPY FAZI (predloha `maklerov-grafika-zakazky`,
          schvaleni R.B. 3. 11.). Do GRAF5 tu byl VYBER (krok UXK3c ho zavedl
          proto, ze az nine cipu se na 390 px zalamovalo do ctyr rad). Cipy se
          vraci, ale uz se NEZALAMUJI: uzky pruh se posouva do strany
          (`rh-a2-chips--scroll`), takze obsah stranky zacina na mobilu dal nad
          prehybem. Jeden stav (`stageFilter`), jeden ovladac — vyber zanikl,
          aby se dve mista teze volby nemohla rozejit.

          Faze je cip jen v TABULCE: v kanbanu je faze sloupec (tentyz duvod,
          jaky zapsal UXK3c).
        */}
        {view === 'table' ? (
          <div
            className="rh-a2-filters rh-a2-chips--scroll"
            role="group"
            aria-label={t('webAdmin.zakazky.phaseFilterLabel')}
            data-testid="stage-chips"
          >
            <button
              type="button"
              className={`rh-a2-chip${stageFilter === 'all' ? ' is-on' : ''}`}
              onClick={() => setStageFilter('all')}
              aria-pressed={stageFilter === 'all'}
              data-testid="stage-chip-all"
            >
              {t('webAdmin.zakazky.filterAll')} <span className="rh-a2-chip__n">{beforeStageFilter.length}</span>
            </button>
            {stageChips.map(({ stage, count }) => (
              <button
                key={stage}
                type="button"
                /* Odstin tecky je TYZ `stageHue`, jaky nese kapsle v radku
                   tabulky i tecka kanbanu (krok GRAF3) — ne druha paleta. */
                className={`rh-a2-chip rh-a2-chip--stage rh-a2-chip--stage-${stageHue(stage) ?? 'none'}${stageFilter === stage ? ' is-on' : ''}`}
                onClick={() => setStageFilter(stageFilter === stage ? 'all' : stage)}
                aria-pressed={stageFilter === stage}
                data-testid={`stage-chip-${stage}`}
              >
                {stageLabel(stage)} <span className="rh-a2-chip__n">{count}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* Krok GRAF5: rada ctyr dlazdic s cisly (viz `stageTiles`). */}
        {view === 'table' ? (
          <div className="rh-a2-tiles" data-testid="zakazky-tiles">
            <div className="rh-a2-tile" data-testid="zakazky-tile-open">
              <span className="rh-a2-tile__label">{t('webAdmin.ekonomikaKancelare.kpiOpenCases')}</span>
              <span className="rh-a2-tile__n">{totalOpen}</span>
            </div>
            {stageTiles.map((tile) => (
              <button
                key={tile.stage}
                type="button"
                className={`rh-a2-tile rh-a2-tile--action${stageFilter === tile.stage ? ' is-on' : ''}`}
                onClick={() => setStageFilter(stageFilter === tile.stage ? 'all' : tile.stage)}
                aria-pressed={stageFilter === tile.stage}
                data-testid={`zakazky-tile-${tile.stage}`}
              >
                <span className="rh-a2-tile__label">{tile.label}</span>
                <span className="rh-a2-tile__n">{tile.count}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* NAD RAMEC PREDLOHY: filtry ze starsich kroku (makler, typ, hledani,
            zrusene, aktualnost). Mockup ukazuje jen rychle filtry, proto jsou
            tyhle sbalene — zadny z nich se ale nerusi. */}
        <details className="rh-a2-more-filters" data-testid="more-filters">
          <summary>{t('webAdmin.zakazky.moreFilters')}</summary>
          <div className="rh-a2-more-filters__body">
            <label className="rh-a2-field">
              <span>{t('webAdmin.kanban.columnBroker')}</span>
              <select
                value={brokerId}
                onChange={(e) => setBrokerId(e.target.value)}
                data-testid="filter-broker"
                aria-label={t('webAdmin.kanban.brokerFilterAriaLabel')}
              >
                <option value="">{t('webAdmin.kanban.allBrokers')}</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>

            {/*
              Krok QAFIX4b bod 3: POBOČKA. Kreslí se jen když kancelář nějakou
              má — kancelář bez poboček by jinak dostala prázdný přepínač.

              TESTID JE `case-filter-office`, NE `filter-office` (oprava e2e 1,
              21. 10. 2026): `filter-office` uz na TEHZE obrazovce nese tlacitko
              „Celá kancelář" v prepinaci „Moje / Celá kancelář" (UXR1, merí to
              `uxr1-redesign.spec.ts`). Dve kopie teze znacky znamenaly, ze
              `getByTestId('filter-office')` sahl na TLACITKO — scenar pak
              hledal `option` v tlacitku a kancelar BEZ pobocek se netvarila
              jako „filtr se nekresli", ale jako pad. Jmeno drzi konvenci
              sousedu (`task-filter-office` v Ukolech, `demand-filter-office`
              v Poptavkach).
            */}
            {officeOptions.length > 0 ? (
              <label className="rh-a2-field">
                <span>{t('webAdmin.settings.team.officeLabel')}</span>
                <select
                  value={officeFilter}
                  onChange={(e) => setOfficeFilter(e.target.value)}
                  data-testid="case-filter-office"
                  aria-label={t('webAdmin.settings.team.officeLabel')}
                >
                  <option value="">{t('webAdmin.zakazky.allOffices')}</option>
                  {officeOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="rh-a2-field">
              <span>{t('webAdmin.kanban.columnType')}</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                data-testid="filter-type"
                aria-label={t('webAdmin.kanban.typeFilterAriaLabel')}
              >
                <option value="all">{t('webAdmin.kanban.allTypes')}</option>
                <option value="brokerage_sale">{CASE_TYPE_LABELS.brokerage_sale}</option>
                <option value="brokerage_rent">{CASE_TYPE_LABELS.brokerage_rent}</option>
              </select>
            </label>

            <label className="rh-a2-field">
              <span>{t('webAdmin.caseListTexts.searchLabel')}</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('webAdmin.caseListTexts.searchPlaceholder')}
                data-testid="case-search"
                aria-label={t('webAdmin.caseListTexts.searchLabel')}
              />
            </label>

            <label className="rh-a2-field">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                data-testid="filter-cancelled"
              />
              <span>{t('webAdmin.kanban.showCancelled')}</span>
            </label>

            {/* Krok UX2: filtr aktuálnosti. Nabídne se, až server pošle data —
                bez nich by zaškrtnutí vyprázdnilo seznam a nikdo by nevěděl proč. */}
            {freshnessByCase.size > 0 ? (
              <label className="rh-a2-field" title={t('webAdmin.caseListFreshness.filterHint')}>
                <input
                  type="checkbox"
                  checked={staleOnly}
                  onChange={(e) => setStaleOnly(e.target.checked)}
                  data-testid="filter-stale"
                />
                <span>{t('webAdmin.caseListFreshness.filterLabel')}</span>
              </label>
            ) : null}
          </div>
        </details>

        {/* Krok UX2 — HROMADNÁ AKCE, NIKDY AUTOMATICKY. */}
        {staleOnly ? (
          <div className="rh-a2-more-filters__body" style={{ marginBottom: 14 }} data-testid="bulk-recheck">
            {bulkEstimate.count === 0 ? (
              <span className="rh-a2-meta">{t('webAdmin.caseListFreshness.bulkNothing')}</span>
            ) : !bulkConfirm ? (
              <button
                type="button"
                className="rh-a2-btn"
                disabled={bulkBusy}
                onClick={() => setBulkConfirm(true)}
                data-testid="bulk-recheck-start"
              >
                {t('webAdmin.caseListFreshness.bulkButton', { count: String(bulkEstimate.count) })}
              </button>
            ) : (
              <div
                className="rh-a2-more-filters__body"
                role="group"
                aria-label={t('webAdmin.caseListFreshness.bulkConfirmTitle', { count: String(bulkEstimate.count) })}
                data-testid="bulk-recheck-confirm"
              >
                <span>
                  <strong>{t('webAdmin.caseListFreshness.bulkConfirmTitle', { count: String(bulkEstimate.count) })}</strong>{' '}
                  {t('webAdmin.caseListFreshness.bulkConfirmBody')}
                  {bulkEstimate.skippedRunning > 0
                    ? ` ${t('webAdmin.caseListFreshness.bulkConfirmSkipped', { count: String(bulkEstimate.skippedRunning) })}`
                    : ''}
                </span>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--primary"
                  disabled={bulkBusy}
                  onClick={runBulkRecheck}
                  data-testid="bulk-recheck-accept"
                >
                  {t('webAdmin.caseListFreshness.bulkConfirmAccept', { count: String(bulkEstimate.count) })}
                </button>
                <button
                  type="button"
                  className="rh-a2-btn"
                  onClick={() => setBulkConfirm(false)}
                  data-testid="bulk-recheck-cancel"
                >
                  {t('webAdmin.caseListFreshness.bulkConfirmCancel')}
                </button>
              </div>
            )}
            {bulkNotice !== null ? (
              <span role="status" className="rh-a2-meta" data-testid="bulk-recheck-notice">
                {bulkNotice}
              </span>
            ) : null}
          </div>
        ) : null}

        {waitingHumanTasksCount > 0 ? (
          <p className="rh-a2-status">
            <button
              type="button"
              className="rh-a2-link"
              data-testid="human-tasks-waiting"
              onClick={() => void navigate({ to: '/k-doplneni' })}
            >
              {t('webAdmin.humanTasks.waiting', { count: String(waitingHumanTasksCount) })}
            </button>
          </p>
        ) : null}

        {loading ? (
          <p role="status" className="rh-a2-status">
            {t('webAdmin.kanban.loading')}
          </p>
        ) : null}
        {/* Krok TOKYFIX7: dokud dotaz nedobehl, obrazovka rika, ze se hleda —
            vysledek na ni jeste neni (tentyz vzor jako `task-search-pending`). */}
        {searchPending ? (
          <p role="status" className="rh-a2-status" data-testid="case-search-pending">
            {t('webAdmin.caseListTexts.searching')}
          </p>
        ) : null}
        {loadError !== null ? (
          <p role="alert" className="rh-a2-error">
            {loadError}
          </p>
        ) : null}
        {actionError !== null ? (
          <p role="alert" className="rh-a2-error" data-testid="action-error">
            {actionError}
          </p>
        ) : null}
        {truncated ? <p className="rh-a2-status">{t('webAdmin.kanban.truncated', { limit: String(CASE_PAGE_LIMIT) })}</p> : null}

        {!loading && loadError === null && filtered.length === 0 ? (
          emptyState
        ) : view === 'table' ? (
          <>
            <div className="rh-a2-tbl-wrap" data-testid="case-table">
              <table className="rh-a2-tbl">
                <caption className="sr-only">{t('webAdmin.kanban.tableCaption')}</caption>
                <thead>
                  <tr>
                    <SortableHeader
                      field="name"
                      label={t('webAdmin.zakazky.columnAddress')}
                      sort={tableSort}
                      onToggle={toggleSort}
                      style={{ width: '28%' }}
                    />
                    <th>{t('webAdmin.kanban.columnType')}</th>
                    <th>{t('webAdmin.kanban.columnBroker')}</th>
                    <th>{t('webAdmin.kanban.columnStage')}</th>
                    <SortableHeader
                      field="dueAt"
                      label={t('webAdmin.caseListTexts.columnNextStep')}
                      sort={tableSort}
                      onToggle={toggleSort}
                      style={{ width: '24%' }}
                    />
                    <SortableHeader
                      field="changedAt"
                      label={t('webAdmin.zakazky.columnLastChange')}
                      sort={tableSort}
                      onToggle={toggleSort}
                    />
                    <th>
                      <span className="sr-only">{t('webAdmin.zakazky.rowMenuLabel')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => {
                    return (
                      <tr key={row.id}>
                        <td data-label={t('webAdmin.zakazky.columnAddress')}>
                          {/*
                            Krok UXV4a2 bod 3 — nahled nemovitosti a postup i v
                            TABULCE (predloha § Pravidla 3 a 5). Obrazek kresli
                            JEDNA komponenta pro cely admin (`PropertyImage`,
                            krok UXV4a bod 4) a procenta JEDNA funkce
                            (`caseProgressView`) — tytez, jake ma karta kanbanu,
                            Dnešek i detail zakazky.
                          */}
                          <div className="rh-a2-tbl__case">
                            <span className="rh-v4-thumb" aria-hidden="true">
                              <PropertyImage
                                photoUrl={row.coverUrl}
                                propertyId={row.propertyId}
                                lat={row.lat}
                                lon={row.lon}
                                size={{ width: 128, height: 128 }}
                                testIdPrefix={`case-table-thumb-${row.id}`}
                              />
                            </span>
                            <span className="rh-a2-tbl__case-text">
                              <button
                                type="button"
                                className="rh-a2-strong"
                                data-testid={`case-table-row-${row.id}`}
                                {...(row.nameHint !== undefined ? { title: row.nameHint } : {})}
                                onClick={() => openCase(row.id)}
                              >
                                {row.name}
                              </button>
                              {row.isDemo ? (
                                <V4Pill tone="accent" testId={`case-table-demo-${row.id}`}>
                                  {t('webAdmin.guide.demo.badge')}
                                </V4Pill>
                              ) : null}
                              {row.description !== null && row.description !== '' ? (
                                <span
                                  className="rh-a2-sub"
                                  {...(row.descriptionFull !== null &&
                                  row.descriptionFull !== undefined &&
                                  row.descriptionFull !== row.description
                                    ? { title: row.descriptionFull }
                                    : {})}
                                >
                                  {row.description}
                                </span>
                              ) : null}
                              <V4Progress
                                view={caseProgressView(row.stage)}
                                testId={`case-table-progress-${row.id}`}
                              />
                            </span>
                          </div>
                        </td>
                        <td data-label={t('webAdmin.kanban.columnType')}>
                          {CASE_TYPE_LABELS[row.type as KanbanCaseType] ?? row.type}
                        </td>
                        <td className="rh-a2-nowrap" data-label={t('webAdmin.kanban.columnBroker')}>
                          {row.ownerName ?? (
                            <span className="rh-a2-badge" data-testid="case-unassigned">
                              {t('webAdmin.kanban.unassigned')}
                            </span>
                          )}
                        </td>
                        <td data-label={t('webAdmin.kanban.columnStage')}>
                          <span className={`rh-a2-badge ${stageBadgeClassA2(row.stage)}`}>{stageLabel(row.stage)}</span>
                        </td>
                        <td data-label={t('webAdmin.caseListTexts.columnNextStep')}>
                          {/* FIX6 bod 7: serverova hodnota z `case_tasks`, a kdyz
                              chybi (dnes vzdy — tabulka je mrtva cesta), ukol
                              maklere z `crm_tasks`. Krok TAB1: to porovnani uz
                              NENI napsane tady — je to `caseBlockerLine`, tataz
                              funkce, jakou pouziva karta kanbanu („co brzdí"). */}
                          {caseBlockerLine({
                            nextTaskTitle: row.nextTaskTitle,
                            crmNextStep: nextStepByCaseId.get(row.id) ?? null,
                          }) ?? t('webAdmin.kanban.noValue')}
                          {/*
                            Krok UXF5 bod 5 (nalez #12): VEDLE TEXTU VZDY
                            TERMIN A ODPOVEDNY MAKLER. Kdyz ukol nema ani
                            jedno, rekne se to vyslovne („Bez termínu" /
                            „Bez makléře") — prazdne misto by si kazdy precetl
                            jako „nevim". Na 390 px je rada pod nazvem kroku
                            (karty `.rh-a2-tbl`, vykresleni resi CSS).
                          */}
                          <span
                            className="rh-a2-meta rh-a2-nextstep__who"
                            data-testid={`case-next-step-who-${row.id}`}
                          >
                            {nextStepDue(nextStepTaskByCaseId.get(row.id)?.dueAt ?? null)}
                            {' · '}
                            {memberName(nextStepTaskByCaseId.get(row.id)?.ownerUserId ?? null) ??
                              t('webAdmin.tasks.noOwner')}
                          </span>
                        </td>
                        <td className="rh-a2-meta rh-a2-nowrap" data-label={t('webAdmin.zakazky.columnLastChange')}>
                          {formatRelativeChange(row.changedAt, now, relativeTexts)}
                        </td>
                        <td>
                          <div className="rh-a2-rowmenu">
                            <button
                              type="button"
                              className="rh-a2-dots"
                              aria-label={t('webAdmin.zakazky.rowMenuLabel')}
                              aria-expanded={openRowMenu === row.id}
                              onClick={() => setOpenRowMenu(openRowMenu === row.id ? null : row.id)}
                              data-testid={`case-row-menu-${row.id}`}
                            >
                              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                                <Ellipsis size={15} strokeWidth={1.75} />
                              </span>
                            </button>
                            {openRowMenu === row.id ? (
                              <div className="rh-a2-menu__pop rh-a2-rowmenu__pop">
                                <button
                                  type="button"
                                  className="rh-a2-menu__row"
                                  onClick={() => {
                                    setOpenRowMenu(null);
                                    openCase(row.id);
                                  }}
                                >
                                  {t('webAdmin.caseListTexts.rowMenuOpen')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Krok GRAF5: stranky predlohy. Bez druhe stranky se pruh neukaze. */}
            {pageCount > 1 ? (
              <nav
                className="rh-a2-pager"
                aria-label={t('webAdmin.zakazky.table.paginationLabel')}
                data-testid="case-table-pager"
              >
                <button
                  type="button"
                  className="rh-a2-chip"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
                  data-testid="case-table-prev"
                >
                  {t('webAdmin.zakazky.table.prev')}
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`rh-a2-chip${n === safePage ? ' is-on' : ''}`}
                    aria-current={n === safePage ? 'page' : undefined}
                    aria-label={t('webAdmin.zakazky.table.pageButtonLabel', { page: n })}
                    onClick={() => setPage(n)}
                    data-testid={`case-table-page-${n}`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="rh-a2-chip"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage(safePage + 1)}
                  data-testid="case-table-next"
                >
                  {t('webAdmin.zakazky.table.next')}
                </button>
              </nav>
            ) : null}
            <p className="rh-a2-meta" style={{ marginTop: 12 }} data-testid="case-table-count">
              {t('webAdmin.zakazky.shownCount', { shown: sortedRows.length, total: totalOpen })}
            </p>
          </>
        ) : (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(e) => void handleDragEnd(e)}>
            <div className="rh-a2-kanban">
              {columns.map(({ stage, items }) => (
                <KanbanColumn
                  key={stage}
                  stage={stage}
                  items={items}
                  ownerName={memberName}
                  displayName={displayName}
                  now={now}
                  onOpen={openCase}
                  dropAllowed={draggingAllowed === null ? undefined : draggingAllowed.includes(stage)}
                  costsByCase={costsByCase}
                  coverByProperty={coverByProperty}
                  briefByProperty={briefByProperty}
                  nextStepByCaseId={nextStepByCaseId}
                />
              ))}
            </div>
          </DndContext>
        )}
      </div>
    </>
  );
}
